/**
 * DOM-based Markup Parser (raw-only)
 *
 * Converts a block element's childNodes into ContentUnit by walking the
 * DOM. Every inline element becomes an indexed tag whose style is the
 * verbatim opening tag in canonical form (see content/canonical-tag.ts).
 *
 * Mirrors the regex-based parser in `content/parser/MarkupParser.ts` and
 * the edge proxy parser, producing byte-identical text + styles for the
 * same source so translation keys match across boundaries.
 */

import type { ContentUnit } from '../content/types.js';
import { serializeOpeningTag, VOID_TAGS } from '../content/canonical-tag.js';

/**
 * Parse a block element into ContentUnit using DOM walking.
 */
export function parseBlockElement(element: Element): ContentUnit {
  const styles: Record<string, string[]> = {};
  let tagIndex = 0;

  function walk(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.nodeValue ?? '';
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return '';
    }

    const el = node as Element;
    const tag = el.tagName.toLowerCase();

    // Collect attributes preserving source insertion order.
    const attrs: Record<string, string> = {};
    for (let i = 0; i < el.attributes.length; i++) {
      const attr = el.attributes[i];
      attrs[attr.name.toLowerCase()] = attr.value;
    }

    tagIndex++;
    const idx = tagIndex;
    styles[String(idx)] = [`raw:${serializeOpeningTag(tag, attrs)}`];

    if (VOID_TAGS.has(tag)) {
      return `<${idx}/>`;
    }

    let inner = '';
    for (let i = 0; i < node.childNodes.length; i++) {
      inner += walk(node.childNodes[i]);
    }
    return `<${idx}>${inner}</${idx}>`;
  }

  let text = '';
  for (let i = 0; i < element.childNodes.length; i++) {
    text += walk(element.childNodes[i]);
  }

  return { text, styles };
}
